import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Save, User, Search, Plus, Trash2, AlertTriangle } from "lucide-react";
import axios from "axios";
import InspectionModal from "./InspectionModal";

const AssignmentModal = ({
  isOpen,
  onClose,
  mode, // 'create', 'edit', 'view'
  selectedAssignment,
  onSave,
  users = [],
  safetyEquipment = [],
  assignments = [],
  preSelectedEquipment = null, // Single equipment object or array of equipment objects
  existingAssignments = { hasUser: false, hasProject: false } // Existing assignments to block fields
}) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [equipmentItems, setEquipmentItems] = useState([{
    id: Date.now(),
    safety_equipment: '',
    searchTerm: '',
    selectedName: '',
    showDropdown: false
  }]);
  const [localSafetyEquipment, setLocalSafetyEquipment] = useState(() => {
    // Ensure initial value is always an array (handle both array and paginated object formats)
    return Array.isArray(safetyEquipment)
      ? safetyEquipment
      : safetyEquipment?.results || [];
  });
  const userDropdownRef = useRef(null);
  const equipmentDropdownRefs = useRef({});
  const [showInspectionConfirmation, setShowInspectionConfirmation] = useState(false);
  const [equipmentWithoutInspection, setEquipmentWithoutInspection] = useState([]);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [currentInspectionIndex, setCurrentInspectionIndex] = useState(0);
  const [inspectionEquipmentList, setInspectionEquipmentList] = useState([]);
  const [completedInspections, setCompletedInspections] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [assignToProjectOnly, setAssignToProjectOnly] = useState(false);

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

  // Check if equipment has valid (not expired) inspection
  const hasValidInspection = (equipment) => {
    // Safety check - if equipment is undefined or null, return false
    if (!equipment) return false;

    let validityDate = null;

    // Check for latest_inspection structure first (new API format)
    if (equipment.latest_inspection?.validity_date) {
      validityDate = equipment.latest_inspection.validity_date;
    }
    // Fallback to old field structure (legacy API format)
    else if (equipment.safety_equipment_date_of_validity) {
      validityDate = equipment.safety_equipment_date_of_validity;
    }

    if (!validityDate) return false;

    // Check if inspection is not expired
    const today = new Date();
    const expiry = new Date(validityDate);
    return expiry >= today;
  };

  // Check if equipment has required inspection data (valid, not expired)
  const hasRequiredInspectionData = (equipment) => {
    // Use the same logic as hasValidInspection - only valid inspections count
    // Expired inspections are treated the same as missing inspections
    return hasValidInspection(equipment);
  };

  // Fetch projects data
  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/project/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Initialize form data
  useEffect(() => {
    if (isOpen) {
      if (mode === 'create') {
        // Set current date and time as default assignment date
        const now = new Date();
        const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

        setFormData({
          user: '',
          project: '',
          assignment_location: '', // Keep for backward compatibility
          notes: '',
          status: 'assigned',
          assignment_date: localDateTime
        });
        setUserSearchTerm('');
        
        // Handle pre-selected equipment from EquipmentManagement
        if (preSelectedEquipment) {
          if (Array.isArray(preSelectedEquipment)) {
            // Multiple equipment items selected
            const equipmentItems = preSelectedEquipment.map(equipment => {
              const brand = equipment.safety_equipment_model_brand || equipment.safety_equipment_brand || '';
              const model = equipment.safety_equipment_model_name || '';
              const serialNumber = equipment.safety_equipment_serial_number ? ` (S/N: ${equipment.safety_equipment_serial_number})` : '';
              const displayName = `${equipment.safety_equipment_type_name || equipment.safety_equipment_type?.name || ''} - ${brand} ${model}${serialNumber}`.trim();
              return {
                id: equipment.id + Date.now(), // Unique ID for React
                safety_equipment: equipment.id,
                searchTerm: displayName,
                selectedName: displayName,
                showDropdown: false
              };
            });
            setEquipmentItems(equipmentItems);
          } else {
            // Single equipment item selected
            const equipment = preSelectedEquipment;
            const brand = equipment.safety_equipment_model_brand || equipment.safety_equipment_brand || '';
            const model = equipment.safety_equipment_model_name || '';
            const serialNumber = equipment.safety_equipment_serial_number ? ` (S/N: ${equipment.safety_equipment_serial_number})` : '';
            const displayName = `${equipment.safety_equipment_type_name || equipment.safety_equipment_type?.name || ''} - ${brand} ${model}${serialNumber}`.trim();
            setEquipmentItems([{
              id: Date.now(),
              safety_equipment: equipment.id,
              searchTerm: displayName,
              selectedName: displayName,
              showDropdown: false
            }]);
          }
        } else {
          // No pre-selected equipment
          setEquipmentItems([{
            id: Date.now(),
            safety_equipment: '',
            searchTerm: '',
            selectedName: '',
            showDropdown: false
          }]);
        }
      } else if (selectedAssignment) {
        // Handle backward compatibility: if assignment_location is a project ID, map it to project
        const adaptedAssignment = { ...selectedAssignment };
        if (selectedAssignment.assignment_location && !selectedAssignment.project) {
          const projectId = parseInt(selectedAssignment.assignment_location);
          if (!isNaN(projectId)) {
            adaptedAssignment.project = projectId;
          }
        }

        // Convert assignment_date to datetime-local format if it exists
        if (adaptedAssignment.assignment_date) {
          const assignmentDate = new Date(adaptedAssignment.assignment_date);
          const localDateTime = new Date(assignmentDate.getTime() - assignmentDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
          adaptedAssignment.assignment_date = localDateTime;
        }

        setFormData(adaptedAssignment);
        
        // Initialize equipment items - will be handled by separate useEffect when safetyEquipment loads
        setEquipmentItems([{
          id: Date.now(),
          safety_equipment: '',
          searchTerm: '',
          selectedName: '',
          showDropdown: false
        }]);
        
        // Initialize user data
        if (users.length > 0) {
          const user = users.find(u => u.id === selectedAssignment.user);
          if (user) {
            const userDisplayName = `${user.first_name} ${user.last_name}`.trim() || user.username;
            setUserSearchTerm(userDisplayName);
          }
        }
      }
      setError(null);
      setShowUserDropdown(false);
    }
  }, [isOpen, selectedAssignment, preSelectedEquipment]);

  // Sync local safety equipment with props
  useEffect(() => {
    // Ensure we always set an array (handle both array and paginated object formats)
    const equipmentData = Array.isArray(safetyEquipment)
      ? safetyEquipment
      : safetyEquipment?.results || [];
    setLocalSafetyEquipment(equipmentData);
  }, [safetyEquipment]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check user dropdown
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      
      // Check equipment dropdowns
      setEquipmentItems(prev => prev.map(item => {
        const dropdownRef = equipmentDropdownRefs.current[item.id];
        if (dropdownRef && !dropdownRef.contains(event.target)) {
          return { ...item, showDropdown: false };
        }
        return item;
      }));
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle safety equipment data updates when selectedAssignment changes
  useEffect(() => {
    if (isOpen && selectedAssignment && safetyEquipment.length > 0) {
      const equipment = safetyEquipment.find(e => e.id === selectedAssignment.safety_equipment);
      if (equipment) {
        const brand = equipment.safety_equipment_model_brand || equipment.safety_equipment_brand || '';
        const model = equipment.safety_equipment_model_name || '';
        const serialNumber = equipment.safety_equipment_serial_number ? ` (S/N: ${equipment.safety_equipment_serial_number})` : '';
        const displayName = `${equipment.safety_equipment_type_name || equipment.safety_equipment_type?.name || ''} - ${brand} ${model}${serialNumber}`.trim();
        setEquipmentItems([{
          id: Date.now(),
          safety_equipment: selectedAssignment.safety_equipment,
          searchTerm: displayName,
          selectedName: displayName,
          showDropdown: false
        }]);
      }
    }
  }, [isOpen, selectedAssignment, safetyEquipment]);

  // Fetch projects when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle user search
  const handleUserSearch = (value) => {
    setUserSearchTerm(value);
    setShowUserDropdown(true);
    
    if (value.trim() === '') {
      setFormData(prev => ({ ...prev, user: '' }));
    }
  };

  // Handle user selection
  const handleUserSelect = (selectedUser) => {
    const userDisplayName = `${selectedUser.first_name} ${selectedUser.last_name}`.trim() || selectedUser.username;
    setUserSearchTerm(userDisplayName);
    setFormData(prev => ({ ...prev, user: selectedUser.id }));
    setShowUserDropdown(false);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.trim();
    const searchTarget = fullName || user.username;
    return searchTarget.toLowerCase().includes(userSearchTerm.toLowerCase());
  });

  // Handle equipment search
  const handleEquipmentSearch = (itemIndex, value) => {
    setEquipmentItems(prev => prev.map((item, index) => 
      index === itemIndex ? {
        ...item,
        searchTerm: value,
        showDropdown: true,
        selectedName: value.trim() === '' ? '' : item.selectedName
      } : { ...item, showDropdown: false }
    ));
  };

  // Handle equipment selection
  const handleEquipmentSelection = (itemIndex, equipmentId) => {
    const equipment = localSafetyEquipment.find(e => e.id === parseInt(equipmentId));
    
    if (equipment) {
      const brand = equipment.safety_equipment_model_brand || equipment.safety_equipment_brand || '';
      const model = equipment.safety_equipment_model_name || '';
      const serialNumber = equipment.safety_equipment_serial_number ? ` (S/N: ${equipment.safety_equipment_serial_number})` : '';
      const displayName = `${equipment.safety_equipment_type_name || equipment.safety_equipment_type?.name || ''} - ${brand} ${model}${serialNumber}`.trim();
      
      setEquipmentItems(prev => prev.map((item, index) => 
        index === itemIndex ? {
          ...item,
          safety_equipment: equipmentId,
          searchTerm: displayName,
          selectedName: displayName,
          showDropdown: false
        } : item
      ));
    }
  };

  // Filter equipment based on search term and availability
  const getFilteredEquipment = (itemIndex) => {
    const currentItem = equipmentItems[itemIndex];
    const searchTerm = currentItem?.searchTerm || '';
    
    // Get selected equipment IDs from other items
    const selectedEquipmentIds = equipmentItems
      .filter((_, index) => index !== itemIndex)
      .map(item => item.safety_equipment)
      .filter(id => id);

    // Filter equipment that is available or currently selected in this assignment
    return localSafetyEquipment.filter(equipment => {
      const brand = equipment.safety_equipment_model_brand || equipment.safety_equipment_brand || '';
      const model = equipment.safety_equipment_model_name || '';
      const equipmentName = `${equipment.safety_equipment_type_name || equipment.safety_equipment_type?.name || ''} - ${brand} ${model}`.trim();
      const serialNumber = equipment.safety_equipment_serial_number || '';
      
      const matchesSearch = !searchTerm || 
        equipmentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        serialNumber.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Equipment is available if:
      // 1. Status is 'available' OR
      // 2. It's the currently selected equipment in edit mode (for this specific item)
      const isCurrentlySelected = mode === 'edit' && equipment.id === parseInt(currentItem?.safety_equipment);
      const isAvailable = equipment.safety_equipment_status === 'available' || isCurrentlySelected;
      const isNotSelectedElsewhere = !selectedEquipmentIds.includes(equipment.id);
      
      return matchesSearch && isAvailable && isNotSelectedElsewhere;
    });
  };

  // Add new equipment item
  const addEquipmentItem = () => {
    setEquipmentItems(prev => [...prev, {
      id: Date.now(),
      safety_equipment: '',
      searchTerm: '',
      selectedName: '',
      showDropdown: false
    }]);
  };

  // Remove equipment item
  const removeEquipmentItem = (itemIndex) => {
    setEquipmentItems(prev => prev.filter((_, index) => index !== itemIndex));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require at least user OR project (or both)
    if (!formData.user && !formData.project) {
      setError('Please select either a user, a project, or both');
      return;
    }

    // Get selected equipment IDs
    const selectedEquipmentIds = equipmentItems
      .map(item => item.safety_equipment)
      .filter(id => id);

    if (selectedEquipmentIds.length === 0) {
      setError('Please select at least one piece of equipment');
      return;
    }

    // Check if all selected equipment has required inspection data
    // Only check for inspections when assigning to a user (not for project-only assignments)
    const equipmentWithoutInspection = [];
    if (formData.user) {
      selectedEquipmentIds.forEach(equipmentId => {
        const equipment = localSafetyEquipment.find(e => e.id === parseInt(equipmentId));
        if (equipment && !hasRequiredInspectionData(equipment)) {
          equipmentWithoutInspection.push(equipment);
        }
      });

      if (equipmentWithoutInspection.length > 0) {
        // Show confirmation modal for missing inspections
        setEquipmentWithoutInspection(equipmentWithoutInspection);
        setShowInspectionConfirmation(true);
        return;
      }
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // Create assignments for each selected equipment
      // NOTE: If BOTH user and project are selected, create TWO separate assignments
      const assignmentPromises = selectedEquipmentIds.flatMap((equipmentId) => {
        const assignments = [];

        // Create user assignment if user is selected
        if (formData.user) {
          const userAssignmentData = {
            assignment_type: 'user',
            safety_equipment: equipmentId,
            user: formData.user,
            project: null, // User assignments don't have project
            assignment_location: formData.assignment_location || null, // Keep for backward compatibility
            notes: formData.notes,
            status: formData.status || 'assigned'
          };

          // Add assignment date if provided
          if (formData.assignment_date) {
            userAssignmentData.assignment_date = formData.assignment_date;
          }

          // Create or update user assignment
          const userPromise = (async () => {
            if (mode === 'edit' && selectedAssignment && selectedAssignment.assignment_type === 'user') {
              return await axios.patch(`${API_BASE_URL}/equipmentassignment/${selectedAssignment.id}/`, userAssignmentData, config);
            } else {
              return await axios.post(`${API_BASE_URL}/equipmentassignment/`, userAssignmentData, config);
            }
          })();

          assignments.push(userPromise);
        }

        // Create project assignment if project is selected
        if (formData.project) {
          const projectAssignmentData = {
            assignment_type: 'project',
            safety_equipment: equipmentId,
            user: null, // Project assignments don't have user
            project: formData.project,
            assignment_location: formData.assignment_location || null, // Keep for backward compatibility
            notes: formData.notes,
            status: formData.status || 'assigned'
          };

          // Add assignment date if provided
          if (formData.assignment_date) {
            projectAssignmentData.assignment_date = formData.assignment_date;
          }

          // Create or update project assignment
          const projectPromise = (async () => {
            if (mode === 'edit' && selectedAssignment && selectedAssignment.assignment_type === 'project') {
              return await axios.patch(`${API_BASE_URL}/equipmentassignment/${selectedAssignment.id}/`, projectAssignmentData, config);
            } else {
              return await axios.post(`${API_BASE_URL}/equipmentassignment/`, projectAssignmentData, config);
            }
          })();

          assignments.push(projectPromise);
        }

        // Note: Equipment status is now automatically updated by backend
        // - User assignments (assignment_type='user') change status to 'issued'
        // - Project assignments (assignment_type='project') do NOT change status

        return assignments;
      });

      await Promise.all(assignmentPromises);
      onSave && await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving assignment:', error);
      setError(error.response?.data?.detail || 'Failed to save assignment');
    } finally {
      setLoading(false);
    }
  };

  // Handle inspection confirmation response
  const handleInspectionConfirmation = (action) => {
    if (action === 'inspect') {
      // Start inspection process
      setInspectionEquipmentList([...equipmentWithoutInspection]);
      setCurrentInspectionIndex(0);
      setCompletedInspections([]);
      setShowInspectionConfirmation(false);
      setShowInspectionModal(true);
    } else if (action === 'assign_anyway') {
      // Proceed with assignment despite missing inspections
      setShowInspectionConfirmation(false);
      proceedWithAssignment();
    } else {
      // Return to form with highlighted missing inspections
      setShowInspectionConfirmation(false);
      // Equipment without inspection will be highlighted in red
    }
  };

  // Proceed with assignment (extracted from handleSubmit)
  const proceedWithAssignment = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // Get selected equipment IDs
      const selectedEquipmentIds = equipmentItems
        .map(item => item.safety_equipment)
        .filter(id => id);

      // Create assignments for each selected equipment
      // NOTE: If BOTH user and project are selected, create TWO separate assignments
      const assignmentPromises = selectedEquipmentIds.flatMap((equipmentId) => {
        const assignments = [];

        // Create user assignment if user is selected
        if (formData.user) {
          const userAssignmentData = {
            assignment_type: 'user',
            safety_equipment: equipmentId,
            user: formData.user,
            project: null, // User assignments don't have project
            assignment_location: formData.assignment_location || null, // Keep for backward compatibility
            notes: formData.notes,
            status: formData.status || 'assigned'
          };

          // Add assignment date if provided
          if (formData.assignment_date) {
            userAssignmentData.assignment_date = formData.assignment_date;
          }

          // Create or update user assignment
          const userPromise = (async () => {
            if (mode === 'edit' && selectedAssignment && selectedAssignment.assignment_type === 'user') {
              return await axios.patch(`${API_BASE_URL}/equipmentassignment/${selectedAssignment.id}/`, userAssignmentData, config);
            } else {
              return await axios.post(`${API_BASE_URL}/equipmentassignment/`, userAssignmentData, config);
            }
          })();

          assignments.push(userPromise);
        }

        // Create project assignment if project is selected
        if (formData.project) {
          const projectAssignmentData = {
            assignment_type: 'project',
            safety_equipment: equipmentId,
            user: null, // Project assignments don't have user
            project: formData.project,
            assignment_location: formData.assignment_location || null, // Keep for backward compatibility
            notes: formData.notes,
            status: formData.status || 'assigned'
          };

          // Add assignment date if provided
          if (formData.assignment_date) {
            projectAssignmentData.assignment_date = formData.assignment_date;
          }

          // Create or update project assignment
          const projectPromise = (async () => {
            if (mode === 'edit' && selectedAssignment && selectedAssignment.assignment_type === 'project') {
              return await axios.patch(`${API_BASE_URL}/equipmentassignment/${selectedAssignment.id}/`, projectAssignmentData, config);
            } else {
              return await axios.post(`${API_BASE_URL}/equipmentassignment/`, projectAssignmentData, config);
            }
          })();

          assignments.push(projectPromise);
        }

        // Note: Equipment status is now automatically updated by backend
        // - User assignments (assignment_type='user') change status to 'issued'
        // - Project assignments (assignment_type='project') do NOT change status

        return assignments;
      });

      await Promise.all(assignmentPromises);
      onSave && await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving assignment:', error);
      setError(error.response?.data?.detail || 'Failed to save assignment');
    } finally {
      setLoading(false);
    }
  };

  // Handle inspection completion
  const handleInspectionSave = () => {
    const currentEquipment = inspectionEquipmentList[currentInspectionIndex];
    setCompletedInspections(prev => [...prev, currentEquipment.id]);
    
    if (currentInspectionIndex < inspectionEquipmentList.length - 1) {
      // Move to next equipment
      setCurrentInspectionIndex(prev => prev + 1);
    } else {
      // All inspections completed, close modal and save assignment
      setShowInspectionModal(false);
      setCurrentInspectionIndex(0);
      setInspectionEquipmentList([]);
      // Refresh equipment data to get updated inspection status
      refreshEquipmentDataAndSave();
    }
  };

  // Handle inspection skip
  const handleInspectionSkip = () => {
    if (currentInspectionIndex < inspectionEquipmentList.length - 1) {
      // Move to next equipment
      setCurrentInspectionIndex(prev => prev + 1);
    } else {
      // All items processed, close modal and return to form
      setShowInspectionModal(false);
      setCurrentInspectionIndex(0);
      setInspectionEquipmentList([]);
      setShowInspectionConfirmation(false);
    }
  };

  // Refresh equipment data and save assignment
  const refreshEquipmentDataAndSave = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      };

      // Refresh safety equipment data
      const safetyEquipmentResponse = await axios.get(`${API_BASE_URL}/safetyequipment/`, config);
      // Handle paginated response - extract results array
      const equipmentData = Array.isArray(safetyEquipmentResponse.data)
        ? safetyEquipmentResponse.data
        : safetyEquipmentResponse.data.results || [];
      setLocalSafetyEquipment(equipmentData);
      
      // Now proceed with assignment creation
      const selectedEquipmentIds = equipmentItems
        .map(item => item.safety_equipment)
        .filter(id => id);

      const assignmentPromises = selectedEquipmentIds.flatMap((equipmentId) => {
        const assignments = [];

        // Create user assignment if user is selected
        if (formData.user) {
          const userAssignmentData = {
            assignment_type: 'user',
            safety_equipment: equipmentId,
            user: formData.user,
            project: null, // User assignments don't have project
            assignment_location: formData.assignment_location || null, // Keep for backward compatibility
            notes: formData.notes,
            status: formData.status || 'assigned'
          };

          // Add assignment date if provided
          if (formData.assignment_date) {
            userAssignmentData.assignment_date = formData.assignment_date;
          }

          // Create or update user assignment
          const userPromise = (async () => {
            if (mode === 'edit' && selectedAssignment && selectedAssignment.assignment_type === 'user') {
              return await axios.patch(`${API_BASE_URL}/equipmentassignment/${selectedAssignment.id}/`, userAssignmentData, config);
            } else {
              return await axios.post(`${API_BASE_URL}/equipmentassignment/`, userAssignmentData, config);
            }
          })();

          assignments.push(userPromise);
        }

        // Create project assignment if project is selected
        if (formData.project) {
          const projectAssignmentData = {
            assignment_type: 'project',
            safety_equipment: equipmentId,
            user: null, // Project assignments don't have user
            project: formData.project,
            assignment_location: formData.assignment_location || null, // Keep for backward compatibility
            notes: formData.notes,
            status: formData.status || 'assigned'
          };

          // Add assignment date if provided
          if (formData.assignment_date) {
            projectAssignmentData.assignment_date = formData.assignment_date;
          }

          // Create or update project assignment
          const projectPromise = (async () => {
            if (mode === 'edit' && selectedAssignment && selectedAssignment.assignment_type === 'project') {
              return await axios.patch(`${API_BASE_URL}/equipmentassignment/${selectedAssignment.id}/`, projectAssignmentData, config);
            } else {
              return await axios.post(`${API_BASE_URL}/equipmentassignment/`, projectAssignmentData, config);
            }
          })();

          assignments.push(projectPromise);
        }

        // Note: Equipment status is now automatically updated by backend
        // - User assignments (assignment_type='user') change status to 'issued'
        // - Project assignments (assignment_type='project') do NOT change status

        return assignments;
      });

      await Promise.all(assignmentPromises);
      onSave && await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving assignment after inspection:', error);
      setError(error.response?.data?.detail || 'Failed to save assignment');
    }
  };

  // Check if equipment lacks inspection
  const isEquipmentMissingInspection = (equipmentId) => {
    return equipmentWithoutInspection.some(eq => eq.id === parseInt(equipmentId));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            {mode === 'create' ? 'New Assignment' : 
             mode === 'edit' ? 'Edit Assignment' : 
             'Assignment Details'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Assignment options info */}
          <div className={`mb-4 p-3 border rounded-md ${
            existingAssignments.hasUser || existingAssignments.hasProject
              ? 'bg-orange-50 border-orange-200'
              : 'bg-blue-50 border-blue-200'
          }`}>
            {existingAssignments.hasUser || existingAssignments.hasProject ? (
              <>
                <p className="text-sm text-orange-800 font-medium">Existing Assignments Detected:</p>
                <ul className="text-xs text-orange-700 mt-1 list-disc list-inside">
                  {existingAssignments.hasUser && <li>Equipment is already assigned to a user</li>}
                  {existingAssignments.hasProject && <li>Equipment is already assigned to a project</li>}
                </ul>
                <p className="text-xs text-orange-600 mt-2">
                  <strong>Note:</strong> You can only add the missing assignment type{existingAssignments.hasUser && existingAssignments.hasProject ? '' : ` (${!existingAssignments.hasUser ? 'User' : 'Project'})`}.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-blue-800 font-medium">Assignment Options:</p>
                <ul className="text-xs text-blue-700 mt-1 list-disc list-inside">
                  <li><strong>User only:</strong> Equipment assigned to specific person</li>
                  <li><strong>Project only:</strong> Equipment stored at project location</li>
                  <li><strong>User + Project:</strong> Person working on specific project</li>
                </ul>
                <p className="text-xs text-blue-600 mt-2">
                  <strong>Note:</strong> You must select at least one option (User or Project).
                </p>
              </>
            )}
          </div>

          {/* User Selection */}
          <div className="relative" ref={userDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign to User <span className="text-gray-400">(optional)</span>
              {existingAssignments.hasUser && (
                <span className="ml-2 text-xs text-orange-600 font-semibold">
                  (Already assigned to user)
                </span>
              )}
            </label>
              <div className="relative">
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => handleUserSearch(e.target.value)}
                  onFocus={() => setShowUserDropdown(true)}
                  placeholder={existingAssignments.hasUser ? "User already assigned" : "Search for user..."}
                  className={`w-full pl-10 pr-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    existingAssignments.hasUser
                      ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                      : 'border-gray-300'
                  }`}
                  required={false}
                  disabled={mode === 'view' || existingAssignments.hasUser}
                />
                <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>

            {showUserDropdown && !existingAssignments.hasUser && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleUserSelect(user)}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 flex items-center"
                    >
                      <User className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{`${user.first_name} ${user.last_name}`.trim() || user.username}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500 text-center">No users found</div>
                )}
              </div>
            )}
            </div>

          {/* Equipment Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Safety Equipment <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {equipmentItems.map((item, index) => (
                <div key={item.id} className="flex items-center space-x-2">
                  <div className="flex-1 relative" ref={el => equipmentDropdownRefs.current[item.id] = el}>
                    <input
                      type="text"
                      value={item.searchTerm}
                      onChange={(e) => handleEquipmentSearch(index, e.target.value)}
                      onFocus={() => setEquipmentItems(prev => prev.map((prevItem, prevIndex) => 
                        prevIndex === index ? { ...prevItem, showDropdown: true } : { ...prevItem, showDropdown: false }
                      ))}
                      placeholder="Search for equipment..."
                      className={`w-full py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        item.safety_equipment && hasValidInspection(localSafetyEquipment.find(eq => eq.id === parseInt(item.safety_equipment)))
                          ? 'border-green-400 bg-green-50 pl-10 pr-4'
                          : item.safety_equipment
                            ? 'border-red-400 bg-red-50 pl-16 pr-4'
                            : 'border-gray-300 pl-10 pr-4'
                      }`}
                      disabled={mode === 'view'}
                    />
                    <div className="absolute left-3 top-2.5 flex items-center">
                      <Search className="h-4 w-4 text-gray-400" />
                      {item.safety_equipment && !hasValidInspection(localSafetyEquipment.find(eq => eq.id === parseInt(item.safety_equipment))) && (
                        <AlertTriangle className="h-4 w-4 text-red-500 ml-1" title="No valid inspection" />
                      )}
                    </div>
                    {item.safety_equipment && (
                      <button
                        type="button"
                        onClick={() => setEquipmentItems(prev => prev.map((prevItem, prevIndex) => 
                          prevIndex === index ? { 
                            ...prevItem, 
                            safety_equipment: '', 
                            searchTerm: '', 
                            selectedName: '' 
                          } : prevItem
                        ))}
                        className="absolute right-3 top-2.5 text-red-500 hover:text-red-700"
                        title="Clear selection"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                    
                    {item.showDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {getFilteredEquipment(index).length > 0 ? (
                          getFilteredEquipment(index).map((equipment) => {
                            const statusBadge = equipment.safety_equipment_status === 'available' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800';
                            const statusText = equipment.safety_equipment_status === 'available' 
                              ? 'Available' 
                              : 'Assigned';
                            
                            return (
                              <button
                                key={equipment.id}
                                type="button"
                                onClick={() => handleEquipmentSelection(index, equipment.id)}
                                className={`w-full text-left px-4 py-2 ${
                                  hasValidInspection(equipment)
                                    ? 'bg-green-50 border-l-4 border-green-400 hover:bg-green-100 focus:bg-green-100' 
                                    : 'bg-red-50 border-l-4 border-red-400 hover:bg-red-100 focus:bg-red-100'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center">
                                      <div className="font-medium">
                                        {equipment.safety_equipment_type_name || equipment.safety_equipment_type?.name}
                                      </div>
                                      {!hasValidInspection(equipment) && (
                                        <AlertTriangle className="h-4 w-4 text-red-500 ml-2" title="No valid inspection" />
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {equipment.safety_equipment_model_brand || equipment.safety_equipment_brand || ''} {equipment.safety_equipment_model_name || ''}
                                      {equipment.safety_equipment_serial_number && (
                                        <span className="ml-2">S/N: {equipment.safety_equipment_serial_number}</span>
                                      )}
                                    </div>
                                    {hasValidInspection(equipment) ? (
                                      <div className="text-xs text-green-600 mt-1">
                                        ✓ Valid until: {equipment.latest_inspection?.validity_date || equipment.safety_equipment_date_of_validity}
                                      </div>
                                    ) : (
                                      <div className="text-xs text-red-600 mt-1">
                                        ⚠️ No valid inspection
                                      </div>
                                    )}
                                  </div>
                                  <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${statusBadge}`}>
                                    {statusText}
                                  </span>
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-4 py-2 text-gray-500 text-center">No available equipment found</div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {equipmentItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeEquipmentItem(index)}
                      className="p-2 text-red-500 hover:text-red-700"
                      disabled={mode === 'view'}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              
              {mode !== 'view' && (
                <button
                  type="button"
                  onClick={addEquipmentItem}
                  className="flex items-center text-blue-600 hover:text-blue-800"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Equipment
                </button>
              )}
            </div>
          </div>

          {/* Assignment Location - Project Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assignment Location (Project) <span className="text-gray-400">(optional)</span>
              {existingAssignments.hasProject && (
                <span className="ml-2 text-xs text-orange-600 font-semibold">
                  (Already assigned to project)
                </span>
              )}
            </label>
            <select
              name="project"
              value={formData.project || ''}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                existingAssignments.hasProject
                  ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                  : 'border-gray-300'
              }`}
              disabled={mode === 'view' || loadingProjects || existingAssignments.hasProject}
            >
              <option value="">{existingAssignments.hasProject ? 'Project already assigned' : 'Select a project...'}</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name} {project.location ? `- ${project.location}` : ''}
                  {project.status ? ` (${project.status})` : ''}
                </option>
              ))}
              {/* Backward compatibility for old assignment_location values */}
              {formData.assignment_location &&
               !projects.find(p => p.id === parseInt(formData.assignment_location)) &&
               isNaN(parseInt(formData.assignment_location)) && (
                <option value="" disabled>
                  Legacy location: {formData.assignment_location}
                </option>
              )}
            </select>
            {loadingProjects && (
              <p className="text-sm text-gray-500 mt-1">Loading projects...</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes..."
              disabled={mode === 'view'}
            />
          </div>

          {/* Assignment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assignment Date & Time
            </label>
            <input
              type="datetime-local"
              name="assignment_date"
              value={formData.assignment_date || ''}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              disabled={mode === 'view'}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                disabled={loading}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {mode === 'edit' ? 'Update Assignment' : 'Create Assignment'}
              </button>
            )}
          </div>
        </form>

        {/* Inspection Confirmation Modal */}
        {showInspectionConfirmation && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-yellow-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">Missing Inspections</h3>
              </div>
              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-3">
                  The following equipment items lack required inspection data:
                </p>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  {equipmentWithoutInspection.map(eq => (
                    <li key={eq.id} className="flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      {eq.safety_equipment_type_name || eq.safety_equipment_type?.name} 
                      {eq.safety_equipment_serial_number && ` (S/N: ${eq.safety_equipment_serial_number})`}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-700">
                  Would you like to perform inspections now?
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleInspectionConfirmation('inspect')}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Yes, Inspect Now
                </button>
                <button
                  onClick={() => handleInspectionConfirmation('assign_anyway')}
                  className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
                >
                  Assign Anyway
                </button>
                <button
                  onClick={() => handleInspectionConfirmation('go_back')}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Inspection Modal */}
        {showInspectionModal && inspectionEquipmentList.length > 0 && (
          <InspectionModal
            isOpen={showInspectionModal}
            onClose={() => {
              setShowInspectionModal(false);
              setCurrentInspectionIndex(0);
              setInspectionEquipmentList([]);
            }}
            equipmentId={inspectionEquipmentList[currentInspectionIndex]?.id}
            equipmentName={`${inspectionEquipmentList[currentInspectionIndex]?.safety_equipment_type_name || inspectionEquipmentList[currentInspectionIndex]?.safety_equipment_type?.name} - ${inspectionEquipmentList[currentInspectionIndex]?.safety_equipment_model_brand || inspectionEquipmentList[currentInspectionIndex]?.safety_equipment_brand || ''} ${inspectionEquipmentList[currentInspectionIndex]?.safety_equipment_model_name || ''}`}
            equipmentSerialNumber={inspectionEquipmentList[currentInspectionIndex]?.safety_equipment_serial_number}
            onSave={handleInspectionSave}
            multiInspectionMode={inspectionEquipmentList.length > 1}
            currentInspectionIndex={currentInspectionIndex}
            totalInspections={inspectionEquipmentList.length}
            onSkip={handleInspectionSkip}
          />
        )}
      </div>
    </div>
  );
};

export default AssignmentModal;